/**
 * The Automation Center's reads and their gating (A4, D9, D11b).
 *
 * FIVE HOOKS IN ONE FILE because they are one request budget, not five
 * independent concerns: the list decides what the detail asks about, and the
 * detail's open tab decides whether the runs — or, since D9, the activity —
 * ask anything at all; since D11b an open run (`run=`) decides whether its
 * detail is read. Splitting them across files would put that budget in
 * several places and let the next change break it in one of them. The keyed
 * page-lifetime cache the two run hooks share lives in `useCachedRead`.
 *
 * `active` IS HOW A HOOK STAYS QUIET. Rules of Hooks leave no choice: the page
 * calls all three on every render, including for a manager who may read none of
 * them. "Not allowed" and "tab not open" therefore have to be parameters rather
 * than absent calls. While `active` is false a hook asks nothing, holds nothing
 * and reports neither loading nor failure — the same shape
 * `useInvoicePaymentsData` uses for its off-screen workspace.
 *
 * That gating is what keeps a manager from spending three guaranteed 403s
 * before seeing a refusal, which is the behaviour `BexioSettingsPage` already
 * refuses for its own admin-only section.
 *
 * NEWEST READ WINS. Each hook carries a sequence number and discards a response
 * whose request has been superseded. Clicking through four automations faster
 * than the network answers must not repaint the fourth with the second's data —
 * the failure `RequirementDetailDrawer` guards against with the same counter.
 *
 * NOTHING IS FILTERED, SORTED OR COUNTED HERE. Rows arrive in the order and
 * quantity the server chose and are handed on untouched; the category counts
 * and the search are folded in the page, over the full list, where they can be
 * read next to the thing they describe.
 *
 * TWO APPLIERS, AND THEY ARE THE WHOLE OF A5b's WRITE PATH HERE (A5b). The
 * toggle's PATCH answers with a complete detail object, and the same answer has
 * to reach two separate states: `items` in one hook and `detail` in another,
 * with no shared store between them. Both appliers take that ONE response and
 * neither of them asks the server anything — a refetch would be a second round
 * trip for a truth already in hand, and on the list it would also throw away
 * whatever page the reader had scrolled to.
 *
 * THE SEQUENCE COUNTERS DO NOT COVER THE APPLIERS, and it is worth being exact
 * about why rather than sprinkling `seq` around. `seq` answers "did a NEWER
 * request from this hook supersede the one that just answered" — it is
 * incremented by `load` and checked by `load`'s own `.then`. A PATCH response
 * is not one of this hook's requests: it arrives from outside, through the
 * applier, and walks past the counter entirely. It also fails differently. The
 * risk is not an out-of-order fetch, it is a response for automation A landing
 * in a panel that is now showing automation B, because the reader clicked on
 * while the write was in flight. That is a question about IDENTITY, not about
 * ordering, so the detail applier asks about identity — see `applyAutomation`.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import {
  fetchAdminAutomation,
  fetchAdminAutomationActivity,
  fetchAdminAutomationRun,
  fetchAdminAutomationRuns,
  fetchAdminAutomations,
} from "../../../api/adminData/automations";
import useCachedRead from "./useCachedRead";

/** Runs per page. Matches the endpoint's own default, so page one sends no limit surprise. */
export const AUTOMATION_RUNS_PAGE_SIZE = 50;

const LIST_ERROR = "Die Automatisierungen konnten nicht geladen werden.";
const DETAIL_ERROR = "Diese Automatisierung konnte nicht geladen werden.";
const RUNS_ERROR = "Die Ausführungen konnten nicht geladen werden.";
const RUN_DETAIL_ERROR = "Diese Ausführung konnte nicht geladen werden.";
const ACTIVITY_ERROR = "Das Protokoll konnte nicht geladen werden.";

/**
 * Every automation, once.
 *
 * One request on mount and one more per explicit retry — never one per row, and
 * never a second one because a selection changed.
 */
export function useAutomationsList({ active }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // True once a list has actually ARRIVED (D9). Before the first answer the
  // hook reports `loading: false` with no items for one render — which looks
  // exactly like an empty registry. The page corrects the URL against the list
  // (an unknown key, a filtered-away one), and doing that against a list that
  // was never read would wipe a perfectly good deep link.
  const [loaded, setLoaded] = useState(false);
  const seq = useRef(0);

  const load = useCallback(() => {
    if (!active) return;
    const mine = seq.current + 1;
    seq.current = mine;
    setLoading(true);
    setError("");
    fetchAdminAutomations()
      .then((res) => {
        if (seq.current !== mine) return;
        setItems(Array.isArray(res?.items) ? res.items : []);
        setLoaded(true);
      })
      .catch((e) => {
        if (seq.current !== mine) return;
        setItems([]);
        setError(e?.message || LIST_ERROR);
      })
      .finally(() => {
        if (seq.current === mine) setLoading(false);
      });
  }, [active]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Fold a fresh detail response into the matching row, without refetching.
   *
   * KEYED, ALWAYS, AND UNCONDITIONALLY APPLIED. Unlike the detail below, a late
   * answer here can never land on the wrong subject: the row it updates is the
   * row whose `key` the response carries, whatever the reader has since
   * selected. A toggle that resolves after the reader moved on must still
   * update the row it changed — that row is on screen, and leaving it showing
   * the old state would be the one visible lie in this flow.
   *
   * ONLY THE SUMMARY'S OWN FIELDS ARE TAKEN. A detail response is a superset of
   * a list row (`AutomationDetailResponse` extends `AutomationSummaryItem`), so
   * assigning it wholesale would quietly grow `stored_config` and `stats_30d`
   * onto one row out of six and make the collection heterogeneous. Copying only
   * the keys the row already has keeps every row the same shape and needs no
   * hand-maintained field list to stay right when either DTO grows.
   */
  const applyAutomation = useCallback((updated) => {
    if (!updated || !updated.key) return;
    setItems((current) =>
      current.map((row) => {
        if (row.key !== updated.key) return row;
        const next = { ...row };
        for (const field of Object.keys(row)) {
          if (Object.prototype.hasOwnProperty.call(updated, field)) {
            next[field] = updated[field];
          }
        }
        return next;
      })
    );
  }, []);

  return { items, loading, error, loaded, reload: load, applyAutomation };
}

/**
 * One automation's detail, for the current selection.
 *
 * Keyed on `key`: a null key asks nothing, which is what mobile does until the
 * reader taps a row.
 */
export function useAutomationDetail({ active, automationKey }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const seq = useRef(0);

  const load = useCallback(() => {
    if (!active || !automationKey) {
      // Nothing is selected any more (D9: a search that matches nothing, a
      // closed sheet). A read still in flight is now about nothing on screen:
      // retire it, so its answer cannot put an automation back into an empty
      // workspace.
      seq.current += 1;
      setDetail(null);
      setError("");
      setLoading(false);
      return;
    }
    const mine = seq.current + 1;
    seq.current = mine;
    setLoading(true);
    setError("");
    fetchAdminAutomation(automationKey)
      .then((res) => {
        if (seq.current !== mine) return;
        setDetail(res);
      })
      .catch((e) => {
        if (seq.current !== mine) return;
        setDetail(null);
        setError(e?.message || DETAIL_ERROR);
      })
      .finally(() => {
        if (seq.current === mine) setLoading(false);
      });
  }, [active, automationKey]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Replace the shown detail with a fresh response — but only if it is still
   * about the automation being shown.
   *
   * THE GUARD IS THE POINT. A toggle's PATCH can be in flight while the reader
   * clicks a different row; nothing blocks that navigation and nothing should.
   * When the answer finally arrives, this panel may already be describing
   * something else, and writing `birthday_email` into a panel titled
   * "Nachweis-Erinnerung" would be a worse failure than the stale value it was
   * trying to fix: the reader would be looking at one automation's name above
   * another automation's state.
   *
   * ASKED AS A FUNCTIONAL UPDATE so the comparison happens against the state
   * React actually holds at that moment, not against a value captured when the
   * callback was created. A `detail` read from the closure would be the state
   * as of the render that started the write, which is exactly the state this
   * guard exists to distrust.
   *
   * `seq` IS DELIBERATELY NOT TOUCHED. It guards against an older FETCH
   * overwriting a newer one, and no fetch of this hook's is in flight while a
   * toggle is clickable — the panel renders "wird geladen …" instead of the
   * tabs while `loading`, so the switch does not exist during a detail read.
   * Bumping the counter here would therefore discard nothing and only suggest a
   * race that cannot occur.
   */
  const applyAutomation = useCallback((updated) => {
    if (!updated || !updated.key) return;
    setDetail((current) =>
      current && current.key === updated.key ? updated : current
    );
  }, []);

  return { detail, loading, error, reload: load, applyAutomation };
}

/** The cache identity of one page of one automation's runs. */
function runsPageId(automationKey, offset) {
  return JSON.stringify([automationKey, offset]);
}

/** The cache identity of one run of one automation. */
function runDetailId(automationKey, runId) {
  return JSON.stringify([automationKey, runId]);
}

/** Whether a cache identity of either kind belongs to this automation. */
function belongsTo(automationKey) {
  return (id) => {
    try {
      return JSON.parse(id)[0] === automationKey;
    } catch (_) {
      return false;
    }
  };
}

function normalizeRunsPage(res) {
  return {
    items: Array.isArray(res?.items) ? res.items : [],
    total: Number(res?.total) || 0,
    limit: Number(res?.limit) || AUTOMATION_RUNS_PAGE_SIZE,
  };
}

/**
 * One page of run history, asked for only once the tab is open.
 *
 * `active` here means "the Ausführungen tab is showing". Before the reader
 * opens it no request goes out at all — a list of six automations must not cost
 * six run histories nobody looked at.
 *
 * THE OFFSET RESETS WHEN THE AUTOMATION CHANGES, and during render rather than
 * from an effect: an effect would let one request go out with the new key and
 * the old page number, so every selection change would cost two requests and
 * the first would be thrown away. Same reasoning as `useInvoiceDashboardData`.
 *
 * KEYED BY AUTOMATION AND PAGE (D11b, closes D9 finding 3). The answer is filed
 * under the automation and the offset it was asked for, and only the entry for
 * what is on screen now is handed out — automation A's runs never appear under
 * B, not even for the one render before B's request leaves, and a late page-1
 * answer never paints over page 2. A page already read is kept for the page's
 * lifetime: reopening the tab, or turning back to page 1, costs nothing.
 * `refresh` (the "Aktualisieren" button) forgets this automation's pages and
 * reads the current one again; `reload` is the retry after a failure.
 */
export function useAutomationRuns({ active, automationKey }) {
  const [offset, setOffset] = useState(0);

  const [lastKey, setLastKey] = useState(automationKey);
  if (automationKey !== lastKey) {
    setLastKey(automationKey);
    setOffset(0);
  }

  const read = useCachedRead({
    active: active && !!automationKey,
    id: automationKey ? runsPageId(automationKey, offset) : null,
    load: () =>
      fetchAdminAutomationRuns(automationKey, {
        limit: AUTOMATION_RUNS_PAGE_SIZE,
        offset,
      }).then(normalizeRunsPage),
  });

  // `key` defaults to the automation on screen. A caller whose answer belongs to
  // a NAMED automation (S1: a review answered before the reader switched away)
  // passes it, so that automation's pages are forgotten — not whichever is open.
  const { refresh: refreshCache } = read;
  const refresh = useCallback(
    (key = automationKey) => {
      if (key) refreshCache(belongsTo(key));
    },
    [refreshCache, automationKey]
  );

  // The total last answered FOR THIS AUTOMATION, so the pager can stay while
  // another of its pages loads — the reader can turn back instead of waiting.
  // Keyed like everything else: another automation's total is never used.
  const page = read.value;
  const [knownTotal, setKnownTotal] = useState({ key: null, total: 0 });
  if (page && (knownTotal.key !== automationKey || knownTotal.total !== page.total)) {
    setKnownTotal({ key: automationKey, total: page.total });
  }
  const total = page
    ? page.total
    : knownTotal.key !== null && knownTotal.key === automationKey
      ? knownTotal.total
      : 0;

  return {
    items: page ? page.items : [],
    total,
    limit: page ? page.limit : AUTOMATION_RUNS_PAGE_SIZE,
    offset,
    loading: read.loading,
    error: read.error ? read.error.message || RUNS_ERROR : "",
    reload: read.reload,
    refresh,
    setOffset,
  };
}

/**
 * One run in full, asked for only while that run is open (D11b, `run=`).
 *
 * `active` means "the Ausführungen tab of THIS automation is showing and the
 * address names a run". The list never asks for a detail: a page of fifty rows
 * costs one request, and a detail is one more request when a row is opened —
 * never one per row.
 *
 * THE ANSWER HAS TO BE ABOUT WHAT WAS ASKED. The server already answers 404 for
 * a run of another automation; an answer that nevertheless names another run
 * or another automation is refused here as "not found" rather than shown — a
 * detail must never put one automation's run under another's name.
 *
 * KEPT FOR THE PAGE'S LIFETIME, keyed by automation and run: back and forward
 * to a run already read cost nothing. A 404 or a failure is not kept, so
 * opening that run again asks again. `forgetAutomation` drops this
 * automation's runs — the run history's "Aktualisieren" calls it, so a run read
 * before the refresh is read fresh, too.
 */
export function useAutomationRunDetail({ active, automationKey, runId }) {
  const read = useCachedRead({
    active: active && !!automationKey && !!runId,
    id: automationKey && runId ? runDetailId(automationKey, runId) : null,
    load: () =>
      fetchAdminAutomationRun(automationKey, runId).then((res) => {
        if (!res || res.id !== runId || res.automation_key !== automationKey) {
          const err = new Error("Ausführung nicht gefunden");
          err.status = 404;
          throw err;
        }
        return res;
      }),
  });

  const { refresh: refreshCache } = read;
  const forgetAutomation = useCallback(
    (key) => {
      if (key) refreshCache(belongsTo(key));
    },
    [refreshCache]
  );

  const notFound = !!read.error && read.error.status === 404;
  return {
    run: read.value,
    loading: read.loading,
    notFound,
    error: read.error && !notFound ? read.error.message || RUN_DETAIL_ERROR : "",
    reload: read.reload,
    forgetAutomation,
  };
}

/**
 * The administrative history of one automation, asked for only once the
 * Protokoll tab is open (D9).
 *
 * SAME SHAPE AS THE RUN HISTORY, AND KEPT APART FROM IT. `active` means "the
 * Protokoll tab is showing"; before that nothing goes out, and a browsed list
 * costs no timeline per automation. Runs answer "what did the automation do",
 * this answers "who changed the automation" — two endpoints, two hooks, two
 * tabs, never one merged list.
 *
 * NO PAGER, BECAUSE THE ENDPOINT HAS NONE. It returns its newest capped page
 * and the full `total`; the tab says so when the two differ rather than
 * pretending the page is everything.
 *
 * `refreshIfShowing` IS THE REGEL-49 HALF OF THE TOGGLE. A switch writes an
 * audit row into exactly this stream, and a reader looking at the Protokoll
 * while switching must see that row without reopening the tab. It reloads only
 * when the answer is for the automation this hook is currently showing AND the
 * tab is open — refs, not the closure, because the toggle's answer arrives
 * through a callback captured when the click happened, which may be several
 * renders and one selection ago.
 *
 * AN ANSWER REMEMBERS WHOSE IT IS. The tab stays mounted once opened, and the
 * workspace is not rebuilt per automation, so the render that opens another
 * automation's Protokoll still holds the previous automation's answer — the
 * request for the new one only leaves in the effect after it. The answer is
 * therefore stored with its key, and an answer for a different key is never
 * handed out: until the right one arrives the tab reads as loading, not as
 * someone else's history.
 */
export function useAutomationActivity({ active, automationKey }) {
  const [result, setResult] = useState({ key: null, items: [], total: 0, error: "" });
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const current = useRef({ active, automationKey });
  current.current = { active, automationKey };

  const load = useCallback(() => {
    if (!active || !automationKey) return;
    const mine = seq.current + 1;
    seq.current = mine;
    setLoading(true);
    fetchAdminAutomationActivity(automationKey)
      .then((res) => {
        if (seq.current !== mine) return;
        setResult({
          key: automationKey,
          items: Array.isArray(res?.items) ? res.items : [],
          total: Number(res?.total) || 0,
          error: "",
        });
      })
      .catch((e) => {
        if (seq.current !== mine) return;
        setResult({
          key: automationKey,
          items: [],
          total: 0,
          error: e?.message || ACTIVITY_ERROR,
        });
      })
      .finally(() => {
        if (seq.current === mine) setLoading(false);
      });
  }, [active, automationKey]);

  useEffect(() => {
    load();
  }, [load]);

  // The LATEST `load`, for the same reason `current` holds the latest props: a
  // write that was clicked on Allgemein and answers after the reader opened the
  // Protokoll must reload with the tab's state as it is now, not as it was.
  const latestLoad = useRef(load);
  latestLoad.current = load;

  const refreshIfShowing = useCallback((key) => {
    const now = current.current;
    if (now.active && now.automationKey && now.automationKey === key) {
      latestLoad.current();
    }
  }, []);

  const own = result.key !== null && result.key === automationKey;
  return {
    items: own ? result.items : [],
    total: own ? result.total : 0,
    error: own ? result.error : "",
    loading: loading || (!!automationKey && !own),
    reload: load,
    refreshIfShowing,
  };
}
